<template>
    <div class="inline-flex items-center">
        <!-- Trigger button -->
        <button
            type="button"
            :class="[triggerButtonClass, 'flex items-center justify-between gap-2 bg-background-basic-1']"
            @click="openModal"
        >
            <div class="flex items-center gap-2">
                <span class="text-content-basic-1">
                    {{ label }}
                </span>
                <span
                    class="w-12 h-6 px-4 inline-flex items-center justify-center rounded-full bg-primary-2 text-content-inverse-1 font-inter-heading-20"
                >
                    {{ selectedCount }}
                </span>
            </div>
            <span class="flex items-center justify-center">
                <FilterIcon :width="16" :height="16" color="text-[#666]"/>
            </span>
        </button>

        <!-- Status select modal -->
        <div
            v-if="isOpen"
            class="fixed inset-0 z-[80] flex items-center justify-center bg-black/50"
        >
            <div class="w-[480px] max-w-full bg-white rounded-02 shadow flex flex-col overflow-hidden">
                <!-- Header -->
                <div class="px-8 py-6 flex items-center justify-between border-b border-[#ECECEC]">
                    <p class="font-inter-heading-14 text-[#171717]">
                        {{ modalTitle }}
                    </p>
                    <button type="button" @click="closeModal">
                        <CloseIcon
                            :width="20"
                            :height="20"
                            color="text-[#171717]"
                            weight="REGULAR"
                        />
                    </button>
                </div>

                <!-- Options -->
                <div class="px-4 py-2 max-h-[380px] overflow-y-auto overflow-x-hidden ds-scrollbar">
                    <button
                        v-for="item in options"
                        :key="item.value"
                        type="button"
                        class="w-full my-2 flex items-center gap-2 px-4 py-3 rounded-01_5 text-left hover:bg-[#F4F4F4]"
                        :class="isSelected(item.value) ? 'bg-[#F4F4F4]' : 'bg-background-basic-1'"
                        @click="toggleOption(item.value)"
                    >
                        <span
                            class="w-4 h-4 flex items-center justify-center rounded-01 border border-[#DDD]"
                            :class="isSelected(item.value)
                                ? 'border-primary-3 bg-primary-3'
                                : 'border-border-basic-3 bg-background-basic-1'"
                        >
                            <CheckIcon
                                v-if="isSelected(item.value)"
                                :width="16"
                                :height="16"
                                color="text-content-inverse-1"
                                weight="FILL"
                            />
                        </span>
                        <span
                            class="flex-1 font-inter-heading-20"
                            :class="isSelected(item.value) ? 'text-primary-3' : 'text-[#666]'"
                        >
                            {{ item.text }}
                        </span>
                    </button>
                </div>

                <!-- Footer buttons -->
                <div class="px-8 py-6 flex items-center gap-2 border-t border-[#ECECEC]">
                    <DesignButton
                        class="flex-1"
                        btnText="Cancel"
                        btnSize="LG"
                        btnType="SECONDARY"
                        :btnDisabled="false"
                        @click-event="closeModal"
                    />
                    <DesignButton
                        class="flex-1"
                        btnText="Apply"
                        btnSize="LG"
                        btnType="PRIMARY"
                        :btnDisabled="false"
                        @click-event="applySelection"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CloseIcon from '@/components/design-system/icon/close.vue';
import CheckIcon from '@/components/design-system/icon/check.vue';
import FilterIcon from '@/components/design-system/icon/filter.vue';
import DesignButton from '@/components/design-system/design-button.vue';

export default {
    components: {
        CloseIcon, 
        CheckIcon, 
        FilterIcon,
        DesignButton,
    },
    props: {
        // v-model: 선택된 status value 배열
        modelValue: {
            type: Array,
            default: () => [],
        },
        // 드롭다운 라벨 (예: "Status")
        label: {
            type: String,
            default: '',
        },
        // 모달 제목 (예: "Select Status")
        modalTitle: {
            type: String,
            default: '',
        },
        // 옵션 목록: [{ text: 'Active', value: 'ACTIVE' }, ...]
        options: {
            type: Array,
            default: () => [],
        },
        // 드롭다운 사이즈: MD / LG (design-dropdown-select 과 동일)
        size: {
            type: String,
            default: 'MD',
            validator: (val) => ['MD', 'LG'].includes(String(val).toUpperCase()),
        },
        // 버튼 width 클래스 (예: 'w-60')
        width: {
            type: String,
            default: 'w-60',
        },
    },
    emits: ['update:modelValue', 'change'],
    data() {
        return {
            isOpen: false,
            tempSelected: [],
            buttonClassMap: {
                LG: 'h-12 px-3 py-1 relative border border-border-basic-2 rounded-01_5 text-left cursor-pointer focus:border-primary-3',
                MD: 'h-10 px-3 py-1 relative border border-border-basic-2 rounded-02 text-left cursor-pointer focus:border-primary-3',
            },
            buttonTextMap: {
                LG: 'font-inter-heading-20 text-content-basic-1',
                MD: 'font-inter-body-3 text-content-basic-1',
            },

            scrollYBackup : 0,
        };
    },
    computed: {
        selectedCount() {
            return Array.isArray(this.modelValue) ? this.modelValue.length : 0;
        },
        widthClass() {
            return this.width;
        },
        triggerButtonClass() {
            const size = String(this.size).toUpperCase();
            const baseClass = this.buttonClassMap[size] || this.buttonClassMap.MD;
            const textClass = this.buttonTextMap[size] || this.buttonTextMap.MD;
            return [this.widthClass, baseClass, textClass].join(' ');
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(newVal) {
                this.tempSelected = Array.isArray(newVal) ? [...newVal] : [];
            },
        },
        isOpen(val) {
            val ? this.lockBodyScroll() : this.unlockBodyScroll();
        },
    },
    beforeUnmount() {
        this.unlockBodyScroll();
    },
    methods: {
        // -----------------------------------------------------------------------------
        lockBodyScroll() {
            // 현재 스크롤 위치 저장
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;

            // 레이아웃 점프 방지 및 스크롤 잠금
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden'; // 보조(데스크톱)
        },
        unlockBodyScroll() {
            // 원복
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';

            // 저장해 둔 위치로 복귀
            window.scrollTo(0, y);
        },
        // -----------------------------------------------------------------------------
        openModal() {
            this.tempSelected = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
            this.isOpen = true;
        },
        closeModal() {
            this.isOpen = false;
        },
        isSelected(value) {
            return this.tempSelected.includes(value);
        },
        toggleOption(value) {
            if (this.isSelected(value)) {
                this.tempSelected = this.tempSelected.filter((v) => v !== value);
            } else {
                this.tempSelected = [...this.tempSelected, value];
            }
        },
        applySelection() {
            const next = Array.isArray(this.tempSelected) ? [...this.tempSelected] : [];
            this.$emit('update:modelValue', next);
            this.$emit('change', next);
            this.isOpen = false;
        },
    },
};
</script>

